import type { HelpSection } from '../helpContent';

const DESK: HelpSection['who'] = ['manager', 'admin', 'hr'];

export const DAYBOARD: HelpSection[] = [
  {
    id: 'desk-basics', group: 'Getting started', title: 'Finding your way around', who: DESK, where: ['desk'],
    body: `
The desktop pages are for managers (levels 7–9), admins and HR. Shift leads and employees use the phone app instead.

- **Tabs** across the top: Day board, Rotation, Week summary, Employees, Messages and Settings. Admins also get Managers & access and System status. **Help** is always at the end.
- **Location** (top right) picks the store you're working on. The app remembers your choice on this computer. For Operations (the office), only Employees and Messages are shown.
- **The week bar** (right end of the tab row on Day board, Rotation and Week summary) shows the week you're on, whether it's a draft or posted, and the arrows to move between weeks. See [[weeks]].
- **Signed in as …** under the logo shows who changes are recorded under. Press **Sign out** when you're done on a shared computer.
- Almost everything saves as you go. There's no Save button to forget.

> HR sees an orange bar on every page: "HR view: you can see everything but not change it." Buttons that change things are greyed out for HR.

On a demo store, the header shows **"Demo store · nothing is texted"** or **"Live Demo · real numbers get real texts"** and a **Demo outbox** button. See [[demo-stores]].
`,
  },
  {
    id: 'roles', group: 'Getting started', title: 'Who can do what', who: DESK, where: ['desk'],
    body: `
What you can do depends on your role and level (set on the Employees page and on Managers & access).

- **Admins (up to 5):** everything at every store, including Settings, Managers & access, System status, transfers and Operations people.
- **Managers (levels 7–9):** schedules, rotation, employees, time off and messages for their own stores. Settings is view-only.
- **Store manager (level 9):** also the only manager who can edit or undo a mid-day change, add another manager, change a manager's level, and tick "Entered in PrismHR".
- **HR:** sees every store but can't change schedules or people. HR can send its own messages (it has private conversations with employees) and tick "Entered in PrismHR" in the payroll box.
- **Shift leads (level 6):** phone app with a view-only Store tab, and they can unlock the counter screen to swap rotation spots.
- **Employees (levels 0–5):** the phone app, with their own shifts only.
`,
  },
  {
    id: 'weeks', group: 'Day board', title: 'Weeks: starting, drafts and posting', who: DESK, where: ['desk'],
    body: `
Every store builds its own weeks (Sunday to Saturday). A new week is a **draft**: employees can't see it until you post it.

### Moving between weeks
- **‹ and ›** step through the weeks that have been started for this store.
- The week button ("Week ending Sat 10/24/2026 · Salem ▾") opens a list of every started week, each marked Draft or Posted. At the top, **Go to a day** opens any date (past or future) straight on that day, so you can look back at how a day was scheduled; **Back to today** returns to today.

### Starting a week
1. Open the week button and pick **+ Start the week ending …**, or press **Start the week ending …** on an empty week.
2. Choose **Copy a whole week**, **Choose days to copy** (same weekday to same weekday), or **Start blank**.
3. For a copy, pick the week under **Copy from**, then press **Create week**.

A copy brings over this store's own people's shifts and breaks, each day's kind of day and any Overnight party. It doesn't copy custom staffing numbers, people borrowed from other stores, archived people, or anyone already scheduled somewhere else on that date.

> You can only start the week after the last one you started (or this week, if it hasn't been started). To work four weeks ahead, start them one after another.

### The week tag
A small tag next to the week shows where it stands. Point at it for details.
- **Draft · employees can't see it**
- **Posted 10/17**: employees can see this week.
- **You have unposted changes since 10/17 · Review**: a shift is different from what was posted (its times, its breaks, or someone added or taken off). Employees still see the posted copy. Click it to see exactly what changed. The tag goes away by itself when every shift matches what was posted again.

### Posting
- **Post week** (on a draft): everyone at the store can now see their shifts for that week in the app.
- **Changes not posted yet** (opens from the yellow tag or **Post changes**): one line per person and day, with the **Posted** shift (what employees see), the shift **Now**, what's different (for example **Only the breaks changed**), and who changed it last and when. Nothing in it has been sent to anyone.
  - **Put back as posted**: tick the lines that changed by mistake and press it. Those shifts go back exactly as posted, breaks too. Nobody is told, because employees already see the posted copy. A line without a tick box says why, for example a shift on another store's board.
  - **Post changes**: employees see the new shifts in the app, with an **"Updated · was 12:00p–8:00p"** tag on the changed days. If you change a day back to how it was first posted, the tag goes away.

Posting never sends texts. To text people, use [[text-schedules]]. Mid-day changes (call-ins, leaving early) show to employees right away, without posting.
`,
  },
  {
    id: 'dayboard', group: 'Day board', title: 'Painting shifts on the day board', who: DESK, where: ['desk'], pages: ['day'],
    body: `
The day board shows one day for one store, in 15-minute blocks. Pick the day with the day buttons at the top (Sun 10/18, Mon 10/19…).

### Adding and changing shifts
1. Press and drag along a person's row to paint a shift.
2. To erase, start your drag on a block that's already filled.
3. Let go: it saves, and breaks and lunches are placed for that person automatically.

To add someone who isn't on today's list, switch the toggle above the names from **Sched** (scheduled people) to **All** (everyone at the store) and paint their row.

### Reading the board
- Under each name: level and hours this week ("L5 · 32h wk"), with warnings like **over 40 hours**, **over max of 25h** or **over max of 4 days**. Tags show **Late 11:30a**, **Left 3:00p**, **Called out**, **Time off** (or **Off 2:00p–6:00p** for part of the day), **Called in**, **@ Gresham** (working at another store) or **From Beaverton** (borrowed from another store).
- The right-hand column shows paid hours and the breaks.
- Red underline = scheduled when they said they're not available. Purple underline = scheduled during approved time off.
- The bottom rows count **On the floor** (red when 3 or fewer) and **On break or lunch** (red when 3 or more) for every 15 minutes.
- Greyed, striped blocks are outside the day's hours and can't be painted.

### The tools above the board
- **Copy from [day] → Copy onto this day**: copies another day's shifts and breaks for this store's own people. What's on this day for them is replaced.
- **Recalculate all breaks**: lays out everyone's breaks again from scratch.
- **Mid-day change…**: see [[midday]].
- **Print this day**: prints the scheduled people in landscape. Buttons and staffing rows aren't printed.
- **Email…**: see [[email-pdf]].
- **Schedule at another store…**: see [[sharing]].
- **Showing 11 AM – 10 PM**, with **← Earlier**, **Later →** and **Fit to schedule**: these only change what you see. They never change anyone's shift.

> A row locked by a mid-day change (left early, called out, arrived late) can't be painted. A level 9 manager or an admin has to undo the change first.
`,
  },
  {
    id: 'kinds-of-day', group: 'Day board', title: 'Kind of day and staffing numbers', who: DESK, where: ['desk'], pages: [],
    body: `
### Kind of day
The buttons **Slow all day**, **Busy all day**, **Slow AM, busy PM** (busy from a time you pick) and **Busy AM, slow PM** (slow from a time) tell the app what the day looks like. They set the busy hours for the rotation and pick which staffing preset fills the day.

### Staffing rows
Under the hours are **Staff needed**, **Leads needed** (level 6) and **Managers needed** (levels 7–9). For each hour:
- The box is how many you need.
- The number next to it is how many are scheduled, counted as the fewest people on the clock in any quarter of that hour. It's green when it matches, yellow when you're short, and red when you have more than needed.

These rows aren't printed.

### Presets
**Modify presets** sets your store's normal numbers per hour (8 AM to midnight) for each kind of day. Use **Copy from** to start one preset from another. Presets save as you type.

### Use Custom
Typing a number on the day board changes that day only. The first time you do, the day gets its own copy of the numbers and a **Use Custom** button appears (selected). Clicking a kind-of-day button shows that preset again, and **Use Custom** brings your own numbers back. Custom numbers never change a preset.

### Busy hours
Switch on **Busy hours** (on the right, level with the day buttons) to see how busy each hour usually is, in a strip just above **Staff needed**. Each hour has a bar (green when quiet, through yellow and orange, to red for the busiest) and its number; the busiest hour is outlined in red and the busiest hours are shaded in the hour row, so you can check you have enough people on.
- **Customers** counts the sales rung up in that hour; **Money** is what they came to.
- **Activity** shows how busy the staff are with hands-on jobs: each race, laser tag game, movie showing, dark ride, HoloGate, mini golf or escape room session that had people in it, plus each sale rung up, counts as one. Hold the pointer over an hour to see what it was made of, for example "3 races (14 drivers), 1 laser tag game (8 players), 12 sales rung up". Races count at the time they really started; laser tag heats at their booked time (or, if the heat's name has no time in it, shortly after its last ticket was sold). While it reads, the strip says "Reading attraction times…" instead of showing numbers. The first time you look back at many days it can take a minute to read them ("Reading attraction times…").
- Pick what it's based on: **Last 5 Fridays** (the same weekday as the day you're on, the default), **Every Friday, last 12 months**, **Same day last year**, **A holiday…** or **A specific date…**. The second box picks another day of the week. **A holiday…** compares with that holiday in past years (**last year**, or the average of the last 2 or 3 years): New Year's Day, Martin Luther King Jr. Day, Presidents' Day, Memorial Day, Juneteenth, July 4, Labor Day, Columbus / Indigenous Peoples' Day, Veterans Day, Thanksgiving, Black Friday, Christmas Eve, Christmas Day and New Year's Eve, each on the day itself. When the day you're on is one of them, the strip starts on it; otherwise the holiday list starts at the next one coming up.
- On the right: the average whole day, the busiest hour and how many days it averaged (hover over it for each day). Days the store had no sales (closed) are left out; holidays stay in.

The numbers come straight from APEX (Salem, Gresham, Beaverton) or the Wunderland POS (Avalon, Milwaukie), read once for each day and kept. The first time you look a long way back it can take a few seconds. Your choice is remembered on your computer, and the strip isn't printed.
`,
  },
  {
    id: 'overnight', group: 'Day board', title: 'Overnight party', who: DESK, where: ['desk'],
    body: `
For an all-night lock-in, tick **Overnight party** next to Fit to schedule, then pick **until** (5:30a to 9:00a, 7:00a to start with).

- The day then runs past 5 AM, and every hour of the night counts on that day: shifts, the rotation and the week's hours.
- The next morning's board starts at the party's end time. The earlier blocks are greyed out and can't be scheduled.
- The counter screen, phones and Store tab stay on the party day until it ends.
- A blue bar on both days explains it. Starting a new week by copying carries it over.

The app won't let you:
- tick it, or pick a later end, while someone is scheduled the next morning before that time (it names them so you can move their shift), or
- untick it, or pick an earlier end, while someone is scheduled past that time.

During the night the rotation works as usual. Close the stations you don't need on the Rotation page ([[closing-stations]]): pick a **from** time (up to 4:45 AM) and leave **until** at Closing to close one for the rest of the night.
`,
  },
  {
    id: 'remove-change', group: 'Day board', title: 'Taking someone off a day (the ×)', who: DESK, where: ['desk'],
    body: `
The **×** next to a shift opens a pop-up with three choices:

- **Delete employee and shift**: takes the person and their shift off the day. Nobody replaces them.
- **Change employee**: gives the same shift and breaks to someone else.
- **Cancel**

### Change employee
The list shows this store's people who aren't working that day (and aren't on all-day time off), best choices first, then fewest hours this week. Each line shows how their hours would change and any warnings:
- **Not available** part of the shift
- **Goes over their 25 h limit** or **4-day limit**
- **Over 40 h this week**
- **Not a lead or manager**, when you're replacing a lead or manager

Pick someone, confirm, and they take the shift. The first person comes off the day.

The × is greyed out for rows locked by a mid-day change, for your people who are working at another store that day (bring them back first), and for Operations people unless you're an admin.
`,
  },
  {
    id: 'midday', group: 'Day board', title: 'Mid-day changes: left early, called out, arrived late, called in', who: DESK, where: ['desk'], pages: [],
    body: `
Use **Mid-day change…** (on the Day board or the Rotation page) when the day changes after the schedule is made. Employees see these changes right away, without posting. Any manager can make one. Only a **level 9 manager or an admin** can edit or undo one.

### Left early or called out
1. Under **Left early**, pick who left.
2. Pick the time they left, or tick **All day** if they called out for the whole shift.
3. Pick the reason: **Sick**, **Slow**, **Personal** or **Injury**.
4. Press **Mark as left** (or **Mark as called out**) and confirm.

The rest of their shift stays on the board, greyed out, and their paid hours drop. With **Sick**, the lost hours are recorded as sick pay and added to [[payroll-box|the payroll box]]. With **Slow**, it counts as a slow-day cut, not against them.

### Arrived late
1. Under **Arrived late**, pick who, the time they arrived, and the reason: **Slow (asked to start later)**, **Personal** or **Injury**. There's no Sick choice, so late arrivals never get sick pay.
2. Press **Mark as late** and confirm.

The time before they arrived is greyed out and unpaid everywhere. Someone can be both late and leave early (leaving after arriving), but a call-out can't also be late.

### Called in
1. Under **Called in**, pick someone who was off, and the start and end times (at least an hour).
2. Press **Add shift**. Their breaks are placed for them only, so the rest of the day isn't reshuffled.

### On the board
Each change shows as a bar, for example "Taylor W. left at 3:00p (sick) · 4 h paid of 7.5 scheduled", with **Edit** and **Undo** (level 9 and admins only). Undoing puts the full shift back and unlocks the row.

> If you pick someone and close the pop-up without pressing the button, it warns you that the change wasn't applied.

Each change also feeds the person's [[attendance]] record.
`,
  },
  {
    id: 'cover', group: 'Day board', title: 'Texting people to cover a shift', who: DESK, where: ['desk'],
    body: `
When someone leaves early or calls out, the Mid-day change pop-up shows **Text people to cover 3:00p–7:00p…** under that line. It appears when the open part is at least an hour long and the day is today or later.

1. Press it. The list shows this store's people who are off that day, aren't on time off, haven't marked those hours "Not available", and get texts. People with the fewest hours this week come first.
2. Untick anyone you don't want to ask. **Adjust times** lets you start later or end earlier (at least an hour).
3. Check the text they'll get, then press **Text … people**.
4. They answer in the app with **I can cover it**. They can't answer "no", so nobody is put on the spot. Answers show under the line as they come in.
5. Press **Give them the shift** next to someone who said yes. You can add a short note for them.

They're added as a called-in shift with breaks, and texted right away ("You're covering 3:00p–7:00p today. Thanks!" plus your note; a later day says the day instead of "today"). Everyone else who was asked gets a text saying it's covered.

Undoing or editing the leaving early cancels a cover request that hasn't been given yet.
`,
  },
  {
    id: 'sharing', group: 'Day board', title: 'Scheduling someone at another store', who: DESK, where: ['desk'],
    body: `
One of your people can work a shift at another store. Their hours still count at your store (their home store), and the other store handles their breaks and rotation.

1. On the Day board, press **Schedule at another store…**
2. Pick **Who**, the **Store**, and the **Start** and **End** times (at least an hour).
3. Press **Schedule…** and confirm. This replaces any other shift they had that day.

- They show in light blue on both boards. Your board shows a bar like "Taylor W. → Gresham, 11:00a–7:00p · hours count here" with **Bring back**, which moves the shift back to your own board.
- The other store sees **From Salem** next to their name and a "Shared to us today" bar.
- Like any change, the person sees it once you post (Post week or Post changes).
- If the person has approved time off that day, the pop-up warns you.
- Shared shifts can't run past 5 AM.

Only the home store's managers (or an admin) can schedule their people somewhere else. The Demo store and the real stores never share people.
`,
  },
  {
    id: 'operations-fillin', group: 'Day board', title: 'Operations people filling in at a store', who: DESK, where: ['desk'],
    body: `
Techs and office staff belong to Operations. When one of them fills in at a store, an **admin** adds them to that store's day:

1. On the store's Day board, press **Add from Operations…** (admins only).
2. Pick the person, the start and the end, then press **Add…** and confirm.

Their store level decides which stations they can work in the rotation. Their hours count at the store where they work that day.

The store's managers see them on that day's board in a row marked "Filling in from Operations". Only an admin can change their hours or take them off the day. Managers can still move their breaks with **Recalculate all breaks**.
`,
  },
  {
    id: 'week-summary', group: 'Week summary', title: 'The week summary', who: DESK, where: ['desk'], pages: ['week'],
    body: `
The Week summary is built automatically from the seven day boards. You can't change shifts here.

- One row per person at this store, one column per day: their times, "Off", or "@ Gresham" when they work at another store that day.
- **Days** is the number of days worked, and **Hours** is paid hours (lunches aren't counted). "32 / 30" means 32 scheduled against the most they want (30). Red means over their limit, or over 40 hours.
- Click **Employee**, **Days** or **Hours** at the top of a column to sort by it.
- The green lines at the top show the last few times schedules were texted, and by whom.

Buttons:
- **Print week**: the table in portrait.
- **Email…**: see [[email-pdf]].
- **Text schedules…**: see [[text-schedules]].
- The small **Text** button on a row texts just that person.
`,
  },
  {
    id: 'text-schedules', group: 'Week summary', title: 'Texting schedules', who: DESK, where: ['desk'],
    body: `
Texts are never sent automatically. To text schedules, open the **Week summary** and press **Text schedules…** (or **Text** on one person's row).

1. Pick **Whole week** or **One day**.
2. Press **Everyone**, or **Only changes since last text** to text just the people whose schedule changed since their last text.
3. Untick anyone you don't want to text. Click a name to see the exact text they'll get.
4. Press **Send … texts…** and confirm. Texts can't be taken back.

Each name then shows **Sent**, **Delivered** or **Not delivered**. If one fails straight away, check the number and press **Retry**. For **Not delivered**, check the number and send that person a new text.

### Things to know
- Texts always show the **posted** schedule, the same one employees see in the app. A draft week can't be texted. If you have unposted changes, a reminder tells you to **Post changes** first.
- People who can't be texted show why: **No mobile number**, **Said no to texts** or **Replied STOP**.
- The text starts "WUNDERLAND SALEM WEEKLY SCHEDULE", lists each day with its date ("Sun 10/18: 11:00a - 7:00p", "Mon 10/19: OFF"), says replies aren't read and to use the app, and ends with the note and "Text STOP to opt out." A one-day text starts "WUNDERLAND SALEM SCHEDULE" and has a shorter note.

HR can look through the list and the previews but can't send.
`,
  },
  {
    id: 'email-pdf', group: 'Week summary', title: 'Emailing PDFs', who: ['manager', 'admin'], where: ['desk'],
    body: `
The **Email…** button on the Day board, Week summary and Rotation sends a PDF from **mywunderland@wunderlandgames.com**. Replies go to your store's own address (for example salem@wunderlandgames.com).

1. Press **Email…** on the page you want to send.
2. Pick what to send: **Which weeks** (Week summary, up to 6) or **Which days** (Day board and Rotation; **Whole week** ticks them all).
3. Under **Send to**, tick people in your store's address book. **Add someone…** adds a name and email. The × removes someone from the book.
4. Add an optional message, then press **Send email**. A green line says who it went to, and the pop-up closes by itself.

The subject is made for you, for example "Salem weekly schedule summary ending 10/24" or "Salem daily rotation 10/22". Every email is logged: **Sent from … lately** at the bottom of the pop-up lists the store's last 15 emails (when, what, to whom, who sent it), and admins see them all in **Managers & access › Change history** under **Emails and texts**.

> If the pop-up says the store's email isn't set up yet, ask an admin. On the Demo store, nothing is really emailed: it goes to the Demo outbox.

Draft weeks can be emailed too, so check the week tag before you send one.
`,
  },
];
